import React, { useEffect, useState } from "react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import {
  postData,
  getData,
  putData,
  deleteData,
} from "../../../services/FetchNodeservices";

export default function Department() {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);

  const [form, setForm] = useState({
    id: null,
    dept_name: "",
    user_ids: [],
  });

  /* ================= FETCH ================= */
  const fetchDepartments = async () => {
    const res = await getData("department/fetch");
    if (res?.status) setDepartments(res.data);
  };

  const fetchUsers = async () => {
    const res = await postData("department/users", {});
    if (res?.status) setUsers(res.data);
  };

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  /* ================= MODALS ================= */
  const openAddModal = () => {
    setForm({ id: null, dept_name: "", user_ids: [] });
    setModalOpen(true);
  };

  const openEditModal = (dept) => {
    setForm({
      id: dept.id,
      dept_name: dept.dept_name,
      user_ids: dept.users ? dept.users.map((u) => u.id) : [],
    });
    setModalOpen(true);
  };

  const toggleUserSelection = (userId) => {
    setForm((prev) => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter((id) => id !== userId)
        : [...prev.user_ids, userId],
    }));
  };

  /* ================= SAVE / DELETE ================= */
  const handleSubmit = async () => {
    if (!form.dept_name || form.user_ids.length === 0) {
      alert("Please enter department & select users");
      return;
    }

    const payload = {
      dept_name: form.dept_name,
      user_ids: form.user_ids,
    };

    let res;
    if (form.id)
      res = await putData(`department/update/${form.id}`, payload);
    else res = await postData("department/add", payload);

    if (res?.status) {
      fetchDepartments();
      setModalOpen(false);
    }
  };

  const confirmDelete = async () => {
    const res = await deleteData(`department/delete/${selectedDept.id}`);
    if (res?.status) {
      fetchDepartments();
      setDeleteModal(false);
      setSelectedDept(null);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-slate-50 pt-20 px-4 md:ml-72 md:px-6">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          Departments @ Morgan
        </h2>

        <button
          onClick={openAddModal}
          className="
            inline-flex items-center gap-2
            px-4 py-2.5 bg-indigo-600 text-white text-xs font-semibold
            rounded-xl shadow-sm hover:bg-indigo-700
            transition-all duration-200 active:scale-[0.98]
            w-fit
          "
        >
          <FaPlus /> Add Department
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-x-auto">
        <table className="min-w-[800px] w-full text-sm">
          <thead>
            <tr className="bg-slate-50/75 border-b border-slate-250 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
              <th className="px-6 py-3.5 text-center w-16 whitespace-nowrap">ID</th>
              <th className="px-6 py-3.5 text-left whitespace-nowrap">Department</th>
              <th className="px-6 py-3.5 text-left whitespace-nowrap">
                Assigned Users
              </th>
              <th className="px-6 py-3.5 text-center w-32 whitespace-nowrap">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {departments.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-6 text-center text-slate-500"
                >
                  No department found
                </td>
              </tr>
            ) : (
              departments.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                  <td className="px-6 py-4 text-center text-slate-500 whitespace-nowrap">{d.id}</td>
                  <td className="px-6 py-4 font-semibold text-slate-800 whitespace-nowrap">
                    {d.dept_name}
                  </td>
                  <td className="px-6 py-4">
                    {d.users && d.users.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {d.users.map((u) => (
                          <span
                            key={u.id}
                            className="px-2.5 py-0.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-150/40 rounded-full font-medium"
                          >
                            {u.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center items-center gap-2.5">
                      <button
                        onClick={() => openEditModal(d)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                        title="Edit Department"
                      >
                        <FaEdit className="text-sm" />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedDept(d);
                          setDeleteModal(true);
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-200"
                        title="Delete Department"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-xl border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800 mb-4">
              {form.id ? "Edit Department" : "Add New Department"}
            </h3>

            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Department Name
                </label>
                <input
                  value={form.dept_name}
                  onChange={(e) =>
                    setForm({ ...form, dept_name: e.target.value })
                  }
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Assign Users
                </label>
                <div className="border border-slate-200 p-2.5 rounded-xl max-h-40 overflow-y-auto bg-slate-50/50 shadow-inner space-y-1">
                  {users.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2.5 py-1.5 px-2 hover:bg-white rounded-lg cursor-pointer transition-colors text-slate-700 font-medium"
                    >
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300"
                        checked={form.user_ids.includes(u.id)}
                        onChange={() => toggleUserSelection(u.id)}
                      />
                      {u.first_name} {u.last_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {form.user_ids.map((id) => {
                const user = users.find((u) => u.id === id);
                return (
                  <span
                    key={id}
                    className="px-2.5 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full font-medium"
                  >
                    {user?.first_name} {user?.last_name}
                  </span>
                );
              })}
            </div>

            <div className="flex justify-end gap-2.5 mt-6">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/45 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-slate-100 w-full max-w-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-2">
              Delete Department
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Are you sure you want to delete{" "}
              <b className="text-slate-800">{selectedDept?.dept_name}</b>?
            </p>

            <div className="flex justify-end gap-2.5">
              <button
                onClick={() => setDeleteModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
